import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useInput, Labeled } from 'react-admin';
import { makeStyles, Select, MenuItem } from '@material-ui/core';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { useForm } from 'react-final-form';
import { DateRangeChoices } from './DateRangeLookups';

const useDateRangeFilterStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '0px',
    marginTop: '-4px',
    '& p': {
      margin: '4px',
      display: 'flex',
      alignSelf: 'flex-start'
    }
  },
  labeled: {
    height: '52px'
  },
  iconButton: {
    borderLeft: '1px solid #E0E0E0',
    borderRadius: '0px'
  },
  icon: {
    fontSize: '18px'
  },
  seperationItem: {
    margin: '4px'
  },
  input: {
    height: '32px',
    '&::before': {
      zIndex: 1
    }
  },
  dateFrom: {
    marginLeft: '10px'
  },
  selectContainer: {
    minWidth: '120px',
    '& > *:only-child': {
      width: '100%'
    }
  }
});

export const ConvertRangeToStartEndDate = range => {
  const newSelectValue = range;
  let startDate, endDate;
  moment.locale('en', {
    week: {
      dow: 1 // Monday is the first day of the week
    }
  });

  if (range === '*' || range === 'custom') {
    startDate = null;
    endDate = null;
  } else if (newSelectValue === 'today') {
    startDate = moment();
    endDate = moment();
  } else {
    if (newSelectValue.startsWith('last_')) {
      const timeValue = newSelectValue.replace('last_', '');
      startDate = moment()
        .subtract(1, timeValue)
        .startOf(timeValue);
      endDate = moment()
        .subtract(1, timeValue)
        .endOf(timeValue);
    } else {
      const splitted = newSelectValue.split('_');
      if (splitted.length > 1) {
        const number = splitted[0];
        const rangeType = splitted[1];
        startDate = moment()
          .subtract(number, rangeType)
          .startOf('day');
        endDate = moment().endOf('day');
      } else {
        startDate = moment().startOf(newSelectValue);
        endDate = moment().endOf(newSelectValue);
      }
    }
  }
  return {
    start: startDate,
    startString:
      startDate != null
        ? moment(startDate)
            .startOf('day')
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null,
    end: endDate,
    endString:
      endDate != null
        ? moment(endDate)
            .endOf('day')
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null
  };
};

/**
 * Date Range Input with 2 date inputs
 * @param {*} props
 */
export const DateRangeFilter = props => {
  const {
    label,
    variant,
    defaultValue,
    source,
    fromSource,
    toSource,
    defaultFrom = null,
    defaultTo = null,
    defaultSelectValue = '*',
    showRange = true,
    autoSelectValues = true,
    formatDate
  } = props;
  const [startDate, setStartDate] = useState(defaultFrom);
  const [endDate, setEndDate] = useState(defaultTo);
  const [selectValue, setSelectValue] = useState(
    defaultSelectValue.indexOf('!') > 0
      ? defaultSelectValue.split('!')[0]
      : defaultSelectValue
  );
  const form = useForm();
  const { isRequired, input } = useInput(props);
  const classes = useDateRangeFilterStyles();

  const handleChange = useCallback(
    (newValue, position) => {
      // If startDate and endDate are null, display 'All Dates' in time select
      if (
        !newValue &&
        ((position === 'start' && !endDate) ||
          (position === 'end' && !startDate))
      ) {
        setSelectValue('*');
      } else {
        setSelectValue('custom');
      }

      if (position === 'start') {
        setStartDate(newValue);
        // If no date is selected in endDate, set the same date as startDate
        if (autoSelectValues && !endDate && moment(newValue).isValid()) {
          setEndDate(newValue);
          if (moment(newValue).isSame(moment(), 'day')) {
            setSelectValue('today');
          }
        }
      } else {
        setEndDate(newValue);
        // If no date is selected in startDate, set the same date as endDate
        if (autoSelectValues && !startDate && moment(newValue).isValid()) {
          setStartDate(newValue);
          if (moment(newValue).isSame(moment(), 'day')) {
            setSelectValue('today');
          }
        }
      }

      if (!moment(newValue).isValid()) {
        input.onChange(null);
      }
    },
    [input, startDate, endDate, autoSelectValues]
  );

  /**
   * Handle change on select for the time value
   */
  const handleChangeSelect = useCallback(event => {
    const newSelectValue = event.target.value;
    setSelectValue(newSelectValue);
    const range = ConvertRangeToStartEndDate(newSelectValue);
    setStartDate(range.start);
    setEndDate(range.end);
  }, []);

  useEffect(() => {
    // If all dates are valid, updates form values with the right format
    if (moment(startDate).isValid() || moment(endDate).isValid()) {
      const startDateString = moment(startDate).isValid()
        ? formatDate
          ? formatDate(startDate)
          : moment(startDate)
              .startOf('day')
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null;
      const endDateString = moment(endDate).isValid()
        ? formatDate
          ? formatDate(endDate)
          : moment(endDate)
              .endOf('day')
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null;

      if (fromSource && toSource) {
        form.change(fromSource, startDateString);
        form.change(toSource, endDateString);
        const fieldNewValue =
          selectValue === 'custom'
            ? `${selectValue}!${startDateString}|${endDateString}`
            : selectValue;
        input.onChange(fieldNewValue);
      } else {
        input.onChange(`${startDateString}|${endDateString}`);
      }
    } else if (startDate === null && endDate === null) {
      // If all dates are null, updates form values with the default value
      if (fromSource && toSource) {
        form.change(fromSource, null);
        form.change(toSource, null);
        input.onChange(selectValue);
      } else {
        input.onChange(`${defaultValue}|${defaultValue}`);
      }
    }
  }, [
    startDate,
    endDate,
    form,
    input,
    source,
    defaultValue,
    fromSource,
    toSource,
    defaultFrom,
    defaultTo,
    selectValue,
    formatDate
  ]);

  const maxDateProps = useMemo(() => {
    return moment(endDate).isValid() ? { maxDate: moment(endDate) } : {};
  }, [endDate]);

  const minDateProps = useMemo(() => {
    return moment(startDate).isValid() ? { minDate: moment(startDate) } : {};
  }, [startDate]);

  return (
    <Labeled
      label={label}
      isRequired={isRequired}
      className={classes.labeled}
      classes={{ value: classes.container }}
    >
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <div className={classes.selectContainer}>
          <Select
            id="upload-date"
            value={selectValue}
            onChange={handleChangeSelect}
            MenuProps={{
              // To override the position of the popover
              // (https://github.com/mui-org/material-ui/issues/7961#issuecomment-631354353)
              getContentAnchorEl: null,
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
          >
            {DateRangeChoices.map(choice => {
              return (
                <MenuItem value={choice.id} key={choice.id} id={choice.id}>
                  {choice.name}
                </MenuItem>
              );
            })}
          </Select>
        </div>
        {(showRange || selectValue === 'custom') && (
          <>
            <KeyboardDatePicker
              className={classes.dateFrom}
              variant={variant}
              onChange={newDate => handleChange(newDate, 'start')}
              value={startDate}
              format="MM/dd/yyyy"
              required={isRequired}
              keyboardIcon={
                <CalendarTodayIcon classes={{ root: classes.icon }} />
              }
              KeyboardButtonProps={{
                classes: { root: classes.iconButton },
                id: 'start-date-button',
                'data-testid': 'start-date-button'
              }}
              InputProps={{
                classes: { root: classes.input },
                placeholder: 'mm/dd/yyyy'
              }}
              classes={{
                root: classes.inputContainer
              }}
              {...maxDateProps}
              data-testid="start-date"
              DialogProps={{
                id: 'start-date-button',
                'data-testid': 'start-date-modal'
              }}
              rightArrowButtonProps={{
                id: 'right-arrow'
              }}
              leftArrowButtonProps={{
                id: 'left-arrow'
              }}
            />
            <p className={classes.seperationItem}>-</p>
            <KeyboardDatePicker
              variant={variant}
              onChange={newDate => handleChange(newDate, 'end')}
              value={endDate}
              format="MM/dd/yyyy"
              required={isRequired}
              keyboardIcon={
                <CalendarTodayIcon classes={{ root: classes.icon }} />
              }
              KeyboardButtonProps={{
                classes: { root: classes.iconButton },
                id: 'end-date-button',
                'data-testid': 'end-date-button'
              }}
              InputProps={{
                classes: { root: classes.input },
                placeholder: 'mm/dd/yyyy'
              }}
              classes={{
                root: classes.inputContainer
              }}
              {...minDateProps}
              data-testid="end-date"
              DialogProps={{
                id: 'end-date-button',
                'data-testid': 'end-date-modal'
              }}
              rightArrowButtonProps={{
                id: 'right-arrow'
              }}
              leftArrowButtonProps={{
                id: 'left-arrow'
              }}
            />
          </>
        )}
      </MuiPickersUtilsProvider>
    </Labeled>
  );
};

export default DateRangeFilter;
